import { useAuth } from "@babackapp/auth-client/hooks";
import logoColors from "@/assets/logo-colors.png";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { jwtDecode } from "jwt-decode";
import useAddShopMemberWithToken from "@babackapp/private-api-client/client/use-add-shop-member-with-token";

export default function SignInPage() {
  const t = useTranslation();
  const navigate = useNavigate();

  const { mutate: addShopMember } = useAddShopMemberWithToken({
    onSuccess: () => {
      navigate("/");
    },
  });
  const schema = z.object({
    email: z.string().email({
      message: t("signin.form.email.error"),
    }),
    password: z.string().min(2, {
      message: t("signin.form.password.error"),
    }),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const formEmail = form.watch("email");

  const token = new URLSearchParams(window.location.search).get("token");
  const emailFromUrl = new URLSearchParams(window.location.search).get("email");

  const auth = useAuth();

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      const { user } = await auth.login(data);
      if (token) {
        const { shopId } = jwtDecode(token as string) as {
          shopId: string;
        };
        addShopMember({
          shopId,
          data: { token, userId: user.id },
        });
      }
    } catch (error) {
      // Error handling is managed by the auth context
      throw error;
    }
  });

  return (
    <>
      <div className="h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 space-y-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img className="mx-auto h-12 w-auto" src={logoColors} alt="Baback" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t("login.login")}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t("misc.or")}{" "}
            <Link
              to={`/auth/sign-up${token || emailFromUrl ? "?" : ""}${
                token ? "token=" + token : ""
              }${emailFromUrl ? "&email=" + emailFromUrl : ""}`}
              className="font-medium text-purple-600 hover:text-purple-500"
            >
              {t("signup.create")}
            </Link>
          </p>
        </div>

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {auth.error && (
            <FormGlobalError
              error={auth.error}
              email={formEmail}
              onResendVerification={auth.resendVerification}
            />
          )}
        </div>

        <div className=" sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <Form {...form}>
              <form onSubmit={handleSubmit} className="space-y-8">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("signin.form.email.title")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("signin.form.password.title")}</FormLabel>
                      <FormControl>
                        <Input {...field} type={"password"} />
                      </FormControl>
                      <p className="text-xs text-gray-600">
                        <Link
                          to={`/auth/forgotpassword`}
                          className="font-medium text-purple-600 hover:text-purple-500"
                        >
                          {t("login.forgotPwd")}
                        </Link>
                      </p>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  className="w-full"
                  type="submit"
                  disabled={auth.isLoading}
                >
                  {t("login.login")}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
